// Maps bundled stack frames ("src_pages_X_tsx.chunk.js:345:11") back to the
// original source ("src/pages/X/index.tsx:124:1") using the .map file webpack
// emits next to each chunk — the same lookup DevTools does. Frames whose map
// can't be loaded (e.g. a build with GENERATE_SOURCEMAP=false) are kept as-is.
//
// Only runs when an error is logged. Maps aren't kept in memory between errors
// (the browser's HTTP cache makes refetching cheap), each map is decoded in a
// single pass that stops after the last line we need, and oversized maps are
// skipped so a vendor bundle can't stall the main thread.

interface ISourceMap {
  sources: (string | null)[];
  sourceRoot?: string | null;
  mappings: string;
}

interface IFrame {
  position: string;
  fileUrl: string;
  line: number;
  column: number;
}

// [generatedColumn, sourceIndex, originalLine, originalColumn] — all 0-based.
type TSegment = [number, number, number, number];

const FETCH_TIMEOUT_MS = 5000;
// ~40 MB of JSON; larger maps are skipped. The production main.js map is ~17 MB.
const MAX_MAP_CHARS = 40_000_000;

// "https://host/static/js/file.js:345:11" -> [url, line, column]
const FRAME_POSITION = /(https?:\/\/[^\s()]+):(\d+):(\d+)/;

const BASE64_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const BASE64_INDEX: Record<string, number> = {};
for (let i = 0; i < BASE64_CHARS.length; i += 1) {
  BASE64_INDEX[BASE64_CHARS[i]] = i;
}

// Files whose map is missing/invalid/too large — don't refetch on every error.
const unavailableMaps = new Set<string>();

const fetchMap = async (fileUrl: string): Promise<ISourceMap | null> => {
  if (unavailableMaps.has(fileUrl)) return null;
  let timer: number | undefined;
  try {
    const controller = new AbortController();
    timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    // webpack names maps "<chunk>.js.map" alongside the chunk.
    const response = await fetch(`${fileUrl}.map`, {
      signal: controller.signal,
    });
    const text = response.ok ? await response.text() : "";
    // A SPA host may answer unknown paths with index.html, so validate the shape.
    const map = text && text.length <= MAX_MAP_CHARS ? JSON.parse(text) : null;
    if (!Array.isArray(map?.sources) || typeof map?.mappings !== "string") {
      unavailableMaps.add(fileUrl);
      return null;
    }
    return map;
  } catch {
    // Network error/timeout may be transient, so don't mark it unavailable.
    return null;
  } finally {
    window.clearTimeout(timer);
  }
};

// Single pass over the VLQ "mappings" string, keeping segments only for the
// wanted (0-based) generated lines. Source/line/column deltas carry across
// lines, so every segment before the last wanted line still has to be read.
const decodeLines = (mappings: string, wanted: Set<number>) => {
  const result = new Map<number, TSegment[]>();
  const lastWanted = Math.max(...Array.from(wanted));
  const fields = [0, 0, 0, 0, 0];
  let fieldCount = 0;
  let value = 0;
  let shift = 0;
  let line = 0;
  let generatedColumn = 0;
  let sourceIndex = 0;
  let originalLine = 0;
  let originalColumn = 0;

  for (let i = 0; i <= mappings.length; i += 1) {
    const char = i < mappings.length ? mappings[i] : ";";

    if (char === "," || char === ";") {
      if (fieldCount > 0) {
        generatedColumn += fields[0];
        // 1-field segments map generated code to no source.
        if (fieldCount >= 4) {
          sourceIndex += fields[1];
          originalLine += fields[2];
          originalColumn += fields[3];
          if (wanted.has(line)) {
            const segments = result.get(line) ?? [];
            segments.push([generatedColumn, sourceIndex, originalLine, originalColumn]);
            result.set(line, segments);
          }
        }
      }
      fieldCount = 0;
      if (char === ";") {
        if (line >= lastWanted) break;
        line += 1;
        generatedColumn = 0;
      }
      continue;
    }

    const digit = BASE64_INDEX[char] ?? 0;
    value += (digit & 31) << shift;
    if (digit & 32) {
      shift += 5;
    } else {
      if (fieldCount < fields.length) {
        fields[fieldCount] = value & 1 ? -(value >>> 1) : value >>> 1;
      }
      fieldCount += 1;
      value = 0;
      shift = 0;
    }
  }
  return result;
};

// Dev:  "webpack://@minimal/material-kit-react/./src/x.tsx" -> "src/x.tsx"
// Prod: CRA lists sources relative to src/, so "common/x.ts" -> "src/common/x.ts"
//       and "../node_modules/axios/x.js" -> "node_modules/axios/x.js"
const normalizeSource = (source: string, sourceRoot: string) => {
  const raw = `${sourceRoot}${source}`;
  if (/^webpack:\/\//.test(raw)) {
    const path = raw.replace(/^webpack:\/\/\/?/, "");
    return path.match(/(?:^|\/)\.\/(.*)$/)?.[1] ?? path;
  }
  return raw.startsWith("../") ? raw.slice(3) : `src/${raw}`;
};

// Stack positions are 1-based; source map positions are 0-based.
const originalPosition = (
  map: ISourceMap,
  segments: TSegment[] | undefined,
  column: number,
) => {
  let match: TSegment | undefined;
  for (const segment of segments ?? []) {
    if (segment[0] > column - 1) break;
    match = segment;
  }
  const source = match ? map.sources[match[1]] : null;
  if (!match || typeof source !== "string") return null;
  const path = normalizeSource(source, map.sourceRoot ?? "");
  return `${path}:${match[2] + 1}:${match[3] + 1}`;
};

const isSameOrigin = (url: string) => {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
};

const parseFrame = (line: string): IFrame | null => {
  const match = line?.match(FRAME_POSITION);
  // Only our own chunks have maps we can fetch (no CORS on CDN scripts).
  if (!match || !isSameOrigin(match[1])) return null;
  return {
    position: match[0],
    fileUrl: match[1],
    line: Number(match[2]),
    column: Number(match[3]),
  };
};

const resolveFile = async (
  fileUrl: string,
  frames: IFrame[],
  resolved: Map<IFrame, string>,
) => {
  const map = await fetchMap(fileUrl);
  if (!map) return;
  const segmentsByLine = decodeLines(
    map.mappings,
    new Set(frames.map((frame) => frame.line - 1)),
  );
  frames.forEach((frame) => {
    const original = originalPosition(
      map,
      segmentsByLine.get(frame.line - 1),
      frame.column,
    );
    if (original) resolved.set(frame, original);
  });
};

export const resolveStackLines = async (lines: string[]) => {
  const frames = lines.map(parseFrame);

  // One fetch + one decode per file, however many frames point into it.
  const framesByFile = new Map<string, IFrame[]>();
  frames.forEach((frame) => {
    if (!frame) return;
    framesByFile.set(frame.fileUrl, [
      ...(framesByFile.get(frame.fileUrl) ?? []),
      frame,
    ]);
  });

  const resolved = new Map<IFrame, string>();
  const pending: Promise<void>[] = [];
  framesByFile.forEach((fileFrames, fileUrl) => {
    pending.push(resolveFile(fileUrl, fileFrames, resolved));
  });
  await Promise.all(pending);

  return lines.map((line, index) => {
    const frame = frames[index];
    const original = frame && resolved.get(frame);
    return frame && original ? line.replace(frame.position, original) : line;
  });
};
